import React, { FC, useMemo } from 'react';
import { Box, Grid, Switch, Typography } from '@mui/material';
import { Controller, FormProvider, UseFormReturn } from 'react-hook-form';
import { ErrorMessage } from 'shared/components/Form/ErrorMessage';
import { AutoSwapFormFields } from 'modules/payments/hooks/use-auto-swap-form';
import { uniq } from 'lodash';
import { CurrencyInput } from 'shared/components/Form/CurrencyInput';
import { EXCHANGE_RATE_NAME_TO_CURRENCY, SWAP_MIN_AMOUNTS, SystemCurrency } from '@okletsplay/core';
import { ExchangeRateDto } from 'app/interfaces/dto';
import { convertReverseCrypto, formatCrypto } from 'shared/utils';

interface SwapFormProps {
  form: UseFormReturn<AutoSwapFormFields>;
  isDisabled: boolean;
  exchangeRates: (ExchangeRateDto | undefined)[];
}

const INITIAL_SWITCH_VALUE = false;

export const AutoSwapForm: FC<SwapFormProps> = ({ form, isDisabled, exchangeRates }) => {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = form;

  const pair = watch('pair');
  const isSettingEnabled = watch('enabled');

  const { buy: buyingCurrency, sell: sellingCurrency } = EXCHANGE_RATE_NAME_TO_CURRENCY[pair];

  const exchangeRate = useMemo(
    () =>
      exchangeRates.find(
        (exchange) => exchange?.from === sellingCurrency && exchange?.to === buyingCurrency
      )?.rate ?? 0,
    [buyingCurrency, exchangeRates, sellingCurrency]
  );

  const formatSuffix = (value: number) => {
    const sellingAmount = formatCrypto(convertReverseCrypto(value, exchangeRate), sellingCurrency);
    return ` ${buyingCurrency} (≈${sellingAmount})`;
  };

  return (
    <FormProvider {...form}>
      <Grid mb={1} container rowSpacing={1.5} columnSpacing={1}>
        <Grid item xs={4} sm={2} display="flex" alignItems="center">
          <Typography>Enabled</Typography>
        </Grid>
        <Grid item xs={8} sm={10} display="flex" justifyContent="flex-start" alignItems="center">
          <Controller
            control={control}
            name="enabled"
            render={({}) => (
              <Switch
                onChange={() => setValue('enabled', !isSettingEnabled)}
                checked={isSettingEnabled === true}
                value={INITIAL_SWITCH_VALUE}
                disabled={isDisabled}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} display="flex" alignItems="center">
          <Typography>When {buyingCurrency} balance goes lower than</Typography>
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="threshold"
            render={({ field }) => (
              <CurrencyInput
                {...field}
                disabled={isDisabled}
                suffix={formatSuffix(field.value)}
                onBlur={() => trigger('purchase')}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} display="flex" alignItems="center">
          <Typography>
            Buy with real time exchange rate (or {SWAP_MIN_AMOUNTS[SystemCurrency.USDT]} USDT)
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="purchase"
            render={({ field }) => (
              <CurrencyInput
                {...field}
                disabled={isDisabled}
                suffix={formatSuffix(field.value)}
                onBlur={() => trigger('threshold')}
              />
            )}
          />
        </Grid>
      </Grid>
      <Box mb={1} display="flex">
        {(errors.threshold?.message || errors.purchase?.message) && (
          <Box mr={1}>
            <ErrorMessage
              error={uniq([errors.threshold?.message, errors.purchase?.message])
                .filter(Boolean)
                .join('. ')}
              withSidePadding={false}
            />
          </Box>
        )}
      </Box>
    </FormProvider>
  );
};
